import {
    Close as CloseIcon,
    FileDownload as FileDownloadIcon,
    SendOutlined as SendOutlinedIcon,
} from '@mui/icons-material';
import { AppBar, Box, Button, Dialog, DialogContent, IconButton, Slide, Toolbar, Typography } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { Dayjs } from 'dayjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { forwardRef, ReactElement, ReactNode, Ref, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ApiService from '../../../../ApiService';
import Column from '../../../../components/Column';
import { InfoMessageContext } from '../../../../components/GlobalInfoMessage';
import Row from '../../../../components/Row';
import { getFilenameForDashboard } from './HatDbExportUtils';
import { usePrintContext } from '../../../../hooks/usePrint';
import { Hat2DbContentSection } from '../dashboard/Hat2DbContentSection';
import { useHatDbContext } from '../../context/HatDbContext';
import { HatDbDataContextProvider } from '../../context/HatDbDataContext';
import { HatExportLogo } from './HatExportLogo';
import { HatMediaType, mediaFilters } from '../../HatMetrics';

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
        children: ReactElement;
    },
    ref: Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export enum ExportMethod {
    download = 'download',
    email = 'email',
}

const exportLabels: {
    readonly [key in ExportMethod]: {
        buttonLabel: string;
        buttonIcon: ReactNode;
    };
} = {
    download: {
        buttonLabel: 'Download PDF',
        buttonIcon: <FileDownloadIcon />,
    },
    email: {
        buttonLabel: 'Email PDF',
        buttonIcon: <SendOutlinedIcon />,
    },
};

function formatRange(start: Dayjs, end: Dayjs) {
    return `${start.format('MMMM DD YYYY')} - ${end.format('MMMM DD YYYY')}`;
}

type HatDbMetadataForEmail = {
    startDate: Dayjs;
    endDate: Dayjs;
};

/**
 * Send an email with the dashboard image attached as a PDF file.
 */
async function sendPDFByEmail(filename: string, pdf: jsPDF, meta: HatDbMetadataForEmail) {
    const formData = new FormData();
    formData.append('exportMethod', 'email');
    formData.append('exportType', 'pdf');
    formData.append('startDate', meta.startDate.toISOString());
    formData.append('endDate', meta.endDate.toISOString());
    formData.append('file', pdf.output('blob'), filename);

    await ApiService.processCampaignPerformanceExport(formData);
}

/**
 * Generate a PDF from the image element (ID: hat-export) in the export dialog.
 */
async function exportPdf(filename: string, exportMethod: ExportMethod, meta: HatDbMetadataForEmail) {
    const containerEl = document.getElementById('hat-export');
    if (containerEl !== null) {
        const pdf = new jsPDF();

        // Within the container, elements annotated with data-pdf-page are rendered on separate pages.
        let pageBlocks = Array.from(containerEl.querySelectorAll<HTMLElement>('*[data-pdf-page]'));
        if (pageBlocks.length === 0) {
            // Fallback to exporting the whole container element.
            pageBlocks = [containerEl];
        }

        for (let i = 0; i < pageBlocks.length; i++) {
            if (i > 0) {
                pdf.addPage();
            }

            // Note: html2canvas is very slow.
            // I have tested using canvas.toDataUrl() and canvas.toBlob() but both are equally slow. Slowness appears
            // be inherent in how html2canvas is re-rendering the DOM. Consider switching to snapDOM or html-to-image
            // package which has been benchmarked as being faster.
            const canvas = await html2canvas(pageBlocks[i], { logging: false });
            const dataUrl = canvas.toDataURL('image/jpeg', 0.8);

            const imageProps = pdf.getImageProperties(dataUrl);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (imageProps.height * pdfWidth) / imageProps.width;
            const pdfImageScaleRatio = 0.9;

            pdf.addImage(dataUrl, 'JPEG', 10, 10, pdfWidth * pdfImageScaleRatio, pdfHeight * pdfImageScaleRatio);
        }

        switch (exportMethod) {
            case ExportMethod.email:
                await sendPDFByEmail(filename, pdf, meta);
                return;
            case ExportMethod.download:
                await pdf.save(filename, { returnPromise: true });
                return;
        }
    } else {
        throw new Error('Image element not found');
    }
}

export function HatDbExportDialog(props: { open: boolean; exportMethod: ExportMethod; onClose(): void }) {
    const { exportMethod, open, onClose } = props;
    const { setPrint } = usePrintContext();
    const { showMessage } = useContext(InfoMessageContext);

    const hatDbContext = useHatDbContext();
    const hatDbCriteria = hatDbContext.hatDbCriteria;

    const [working, setWorking] = useState<boolean>(false);

    // Date range header for the export.
    const rangeHeader = useMemo(() => {
        const { currentRangeStart, currentRangeEnd, previousRangeStart, previousRangeEnd, mediaFilter } = hatDbCriteria;

        let header = formatRange(currentRangeStart, currentRangeEnd);
        if (previousRangeStart && previousRangeEnd) {
            header += ' // ' + formatRange(previousRangeStart, previousRangeEnd);
        }
        if (mediaFilter !== HatMediaType.allMedia) {
            header += ' // ' + mediaFilters[mediaFilter].label;
        }

        return header;
    }, [hatDbCriteria]);

    // Function to perform the PDF/email export.
    const handleExportPdf = useCallback(async () => {
        setWorking(true);

        const filename = getFilenameForDashboard(hatDbContext);

        try {
            const meta = {
                startDate: hatDbCriteria.currentRangeStart,
                endDate: hatDbCriteria.currentRangeEnd,
            };

            await exportPdf(filename, exportMethod, meta);

            switch (exportMethod) {
                case ExportMethod.download:
                    showMessage('success', `Downloading: ${filename}`);
                    break;
                case ExportMethod.email:
                    showMessage('success', 'Your report has been emailed to you!');
                    break;
            }

            onClose();
        } catch (err) {
            showMessage('error', 'There was a problem generating the export');
            console.log(err);
        } finally {
            setWorking(false);
        }
    }, [exportMethod, hatDbContext, hatDbCriteria, showMessage, onClose]);

    useEffect(() => {
        setPrint(open);

        if (open === true) {
            setTimeout(() => {
                // Trigger this to rerender Apex Charts
                window.dispatchEvent(new Event('resize'));
            }, 1000);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Note: the dialog needs its own data context. Without this context, a maximum update depth error will be emitted
    // as the dashboard widgets put data on the HatDbDataContext, which is overwritten by the dialog widgets, which
    // re-renders the dashboard widgets, ad inifinium.
    return (
        <HatDbDataContextProvider>
            <Dialog
                fullWidth
                maxWidth="xl"
                open={open}
                onClose={onClose}
                slots={{
                    transition: Transition,
                }}
            >
                <AppBar sx={{ position: 'relative', paddingRight: '0 !important' }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={onClose}
                            aria-label="close"
                            sx={{ color: (theme) => theme.palette.action.active }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            {exportMethod === 'download' ? 'Export PDF' : 'Email PDF'}
                        </Typography>
                        <Button
                            autoFocus
                            variant="contained"
                            color="primary"
                            onClick={handleExportPdf}
                            startIcon={exportLabels[exportMethod].buttonIcon}
                            disabled={working}
                        >
                            {exportLabels[exportMethod].buttonLabel}
                        </Button>
                    </Toolbar>
                </AppBar>
                <DialogContent
                    dividers
                    sx={{
                        pt: 3,
                        pb: 5,
                    }}
                >
                    <Box id="hat-export">
                        <Hat2DbContentSection
                            exportHeader={
                                <Column justifyContent="center" alignItems="center" pb={2}>
                                    <Row justifyContent="center" alignItems="center">
                                        <Typography variant="h5">{rangeHeader}</Typography>
                                    </Row>

                                    <HatExportLogo
                                        agencyId={hatDbCriteria.agencyId}
                                        advertiserId={hatDbCriteria.advertiserId}
                                    />
                                </Column>
                            }
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </HatDbDataContextProvider>
    );
}
